import { useEffect, useState } from 'react'

/**
 * True while the page is being scrolled down, false again as soon as it
 * is scrolled back up or returns to the top.
 *
 * @param {object}  options
 * @param {number}  options.threshold px of travel ignored in either
 *                                    direction, so a jittery trackpad or
 *                                    an overscroll bounce doesn't flicker
 * @param {number}  options.topOffset px from the top within which the
 *                                    result is always false
 */
const useHideOnScroll = ({ threshold = 8, topOffset = 72 } = {}) => {
    const [hidden, setHidden] = useState(false)

    useEffect(() => {
        let lastY = window.scrollY
        let frame = 0

        const update = () => {
            frame = 0
            const y = window.scrollY

            if (y <= topOffset) {
                setHidden(false)
                lastY = y
                return
            }

            if (Math.abs(y - lastY) < threshold) return

            setHidden(y > lastY)
            lastY = y
        }

        const schedule = () => {
            if (!frame) frame = window.requestAnimationFrame(update)
        }

        window.addEventListener('scroll', schedule, { passive: true })

        return () => {
            window.removeEventListener('scroll', schedule)
            if (frame) window.cancelAnimationFrame(frame)
        }
    }, [threshold, topOffset])

    return hidden
}

export default useHideOnScroll
